import React, { useState } from 'react';
import { auth } from './firebaseConfig';
import { signOut } from 'firebase/auth';
import { createCheckoutSession } from './firestore';

interface SubscribePageProps {
  darkMode: boolean;
}

type PlanKey = 'monthly' | 'lifetime';

const plans: { key: PlanKey; label: string; price: string; description: string; priceEnvKey: string }[] = [
  {
    key: 'monthly',
    label: 'Monthly',
    price: '$9.95 / month',
    description: 'Cancel anytime',
    priceEnvKey: 'REACT_APP_STRIPE_PRICE_MONTHLY',
  },
  {
    key: 'lifetime',
    label: 'Lifetime',
    price: '$55 once',
    description: 'Pay once, own it forever',
    priceEnvKey: 'REACT_APP_STRIPE_PRICE_LIFETIME',
  },
];

const SubscribePage: React.FC<SubscribePageProps> = ({ darkMode }) => {
  const [selected, setSelected] = useState<PlanKey>('monthly');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubscribe = async () => {
    const user = auth.currentUser;
    if (!user) return;

    const priceId = selected === 'monthly'
      ? process.env.REACT_APP_STRIPE_PRICE_MONTHLY
      : process.env.REACT_APP_STRIPE_PRICE_LIFETIME;

    if (!priceId) {
      setError('Price not configured. Please contact support.');
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      const url = await createCheckoutSession(user.uid, priceId, user.email ?? undefined);
      window.location.assign(url);
    } catch (err: any) {
      setError(err?.message || 'Something went wrong. Please try again.');
      setIsLoading(false);
    }
  };

  const handleLogOut = async () => {
    await signOut(auth);
  };

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 ${darkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
      <div className={`max-w-md w-full space-y-6 ${darkMode ? 'bg-gray-800' : 'bg-white'} p-10 rounded-xl shadow-2xl`}>
        <div className="text-center">
          <img
            src="/images/title_art.png"
            alt="Spanish in 10 Minutes a Day"
            className="mx-auto h-24 w-auto mb-4"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
          <h2 className={`text-3xl font-extrabold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Get access
          </h2>
          <p className={`mt-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Unlimited access to all 24 audio lessons. Learn at your own pace and pick up where you left off.
          </p>
        </div>

        {/* Plan selector */}
        <div className="grid grid-cols-2 gap-3">
          {plans.map(plan => (
            <button
              key={plan.key}
              onClick={() => setSelected(plan.key)}
              className={`p-4 rounded-lg border-2 text-left transition-colors duration-150 ${
                selected === plan.key
                  ? 'border-indigo-500 bg-indigo-50 ' + (darkMode ? 'bg-indigo-900/30' : 'bg-indigo-50')
                  : darkMode
                    ? 'border-zinc-600 hover:border-zinc-400'
                    : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className={`text-sm font-semibold ${selected === plan.key ? 'text-indigo-600' : darkMode ? 'text-white' : 'text-gray-900'}`}>
                {plan.label}
              </div>
              <div className={`text-lg font-bold mt-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {plan.price}
              </div>
              <div className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {plan.description}
              </div>
            </button>
          ))}
        </div>

        {error && (
          <div className="text-red-500 text-sm text-center">{error}</div>
        )}

        <button
          onClick={handleSubscribe}
          disabled={isLoading}
          className={`w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white ${
            isLoading
              ? 'bg-indigo-400 cursor-not-allowed'
              : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
          } transition-colors duration-200`}
        >
          {isLoading ? (
            <span className="flex items-center space-x-2">
              <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span>Opening checkout...</span>
            </span>
          ) : (selected === 'monthly' ? 'Subscribe' : 'Buy lifetime access')}
        </button>

        <div className="text-center">
          <button
            onClick={handleLogOut}
            className={`text-sm ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'} underline`}
          >
            Log out
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscribePage;
